import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { ArrowDropDown, MoreVert } from '@material-ui/icons';
import {
  first, get, getOr, map,
} from 'lodash/fp';
import { formatPhoneNumber } from '../../common/utils';

const S = {
  Wrapper: styled.div`
    display: flex;
    align-items: center;
    padding: 14px 8px;
    background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.38)};
    text-align: center;
  `,
  Group: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28%;
    padding: 0 8px;
  `,
  GroupName: styled.span`
    margin-right: 4px;
    letter-spacing: -1.5px;
  `,
  IconButton: styled(IconButton)`
    padding: 0;
  `,
  ArrowDropDown: styled(ArrowDropDown)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.25rem;
  `,
  ClazzNumber: styled.span`
    width: 10%;
  `,
  GuardianName: styled.span`
    width: 20%;
  `,
  Relationship: styled.span`
    width: 12%;
  `,
  GuardianPhoneNumber: styled.span`
    width: 24%;
  `,
  More: styled.span`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 6%;
  `,
  MoreVert: styled(MoreVert)`
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.5rem;
  `,
  GroupItem: styled(MenuItem)`
    min-width: 90px;
    min-height: 32px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    cursor: auto;
  `,
  MenuItem: styled(MenuItem)`
    min-width: 90px;
    min-height: 40px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
    &.Mui-selected:hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
};

const IndividualInfo = ({ className, target, openDialog }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = useCallback(event => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const deleteIndividualResponse = useCallback(() => {
    openDialog();

    handleClose();
  }, [handleClose, openDialog]);

  return (
    <S.Wrapper className={className}>
      <S.Group>
        <S.GroupName>
          {get('clazzGroup', target)
            ? `${get('clazzGroup.grade', target)}학년 ${get('clazzGroup.clazzName', target)}반`
            : getOr('미지정', 'name', first(get('customGroups', target)))}
        </S.GroupName>
        <S.IconButton type="button" name="groupList" onClick={handleClick}>
          <S.ArrowDropDown />
        </S.IconButton>
        <Menu
          id="groupList"
          getContentAnchorEl={null}
          anchorEl={get('name', anchorEl) === 'groupList' ? anchorEl : null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={get('name', anchorEl) === 'groupList'}
          onClose={handleClose}
        >
          {get('clazzGroup', target) && (
            <S.GroupItem onClick={handleClose} disableRipple>
              {`${get('clazzGroup.grade', target)}학년 ${get('clazzGroup.clazzName', target)}반`}
            </S.GroupItem>
          )}
          {map(customGroup => (
            <S.GroupItem key={customGroup.id} onClick={handleClose} disableRipple>
              {customGroup.name}
            </S.GroupItem>
          ), get('customGroups', target))}
        </Menu>
      </S.Group>
      <S.ClazzNumber>
        {`${get('clazzNumber', target) || '-'}번`}
      </S.ClazzNumber>
      <S.GuardianName>
        {{
          mother: '모',
          father: '부',
          grand_mother: '조모',
          grand_father: '조부',
          etc: '기타',
        }[getOr('etc', 'primaryGuardian.relationship', target)]}
      </S.GuardianName>
      <S.Relationship>
        {getOr('-', 'name', target)}
      </S.Relationship>
      <S.GuardianPhoneNumber>
        {formatPhoneNumber(getOr('', 'primaryGuardian.phoneNumber', target))}
      </S.GuardianPhoneNumber>
      <S.More>
        <S.IconButton type="button" name="menu" onClick={handleClick}>
          <S.MoreVert />
        </S.IconButton>
      </S.More>
      <Menu
        id="menu"
        getContentAnchorEl={null}
        anchorEl={get('name', anchorEl) === 'menu' ? anchorEl : null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={get('name', anchorEl) === 'menu'}
        onClose={handleClose}
      >
        <S.MenuItem onClick={deleteIndividualResponse}>개별응답 삭제</S.MenuItem>
      </Menu>
    </S.Wrapper>
  );
};

IndividualInfo.propTypes = {
  className: PropTypes.string,
  target: PropTypes.shape({
    cid: PropTypes.number,
    clazzGroup: PropTypes.shape({
      id: PropTypes.number,
      grade: PropTypes.number,
      clazzName: PropTypes.string,
    }),
    customGroups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      isUnspecifiedGroup: PropTypes.bool,
    })),
    clazzNumber: PropTypes.number,
    name: PropTypes.string,
    recorded: PropTypes.bool,
  }),
  openDialog: PropTypes.func.isRequired,
};

IndividualInfo.defaultProps = {
  className: undefined,
  target: null,
};

export default IndividualInfo;
